import { useEffect, useRef, useState } from 'react';
import type { ServerMessage } from '@cixing/shared';
import { TranslationWsClient, type ConnectionState } from '../ws/client.js';

/**
 * 質問モード(日本語→中国語)のオーバーレイ。
 *
 * 実習中に講師へ質問するための逆方向通訳。日本語で話すと中国語訳が大きく表示され、
 * そのまま画面を講師に見せて読んでもらう使い方を想定している。
 * 講義本編の zh→ja セッションは裏で維持したまま、専用の WS を1本開いて使う。
 * ここでのやり取りは「🙋 質問:」の印付きで本編と同じ講義メモへ時系列で残る
 * (質問が残らないと、直後の講師の返答が何の話か分からなくなるため)。
 */

interface Exchange {
  segmentId: string;
  ja: string;
  zh: string;
  final: boolean;
}

export function QuestionMode({
  clientSessionId,
  device,
  onClientChange,
  onClose,
}: {
  /** 本編と同じ ID を渡す。質問のやり取りが同じ講義メモへ時系列で残る。 */
  clientSessionId: string;
  /** 講義メモの持ち主情報(本編と同じもの)。 */
  device: { deviceId?: string; deviceName?: string };
  /** 音声フレームの送り先を切り替えるため、開いたクライアントを親へ渡す。null で解除。 */
  onClientChange: (client: TranslationWsClient | null) => void;
  onClose: () => void;
}): JSX.Element {
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [conn, setConn] = useState<ConnectionState>('connecting');
  const clientRef = useRef<TranslationWsClient | null>(null);

  useEffect(() => {
    const handleMessage = (message: ServerMessage): void => {
      if (
        message.type !== 'source.partial' &&
        message.type !== 'source.final' &&
        message.type !== 'translation.partial' &&
        message.type !== 'translation.final'
      ) {
        return;
      }
      setExchanges((prev) => {
        const next = [...prev];
        let item = next.find((x) => x.segmentId === message.segmentId);
        if (!item) {
          item = { segmentId: message.segmentId, ja: '', zh: '', final: false };
          next.push(item);
        } else {
          item = { ...item };
          next[next.findIndex((x) => x.segmentId === message.segmentId)] = item;
        }
        if (message.type === 'source.partial' || message.type === 'source.final') {
          item.ja = message.text;
        } else {
          item.zh = message.text;
          if (message.type === 'translation.final') item.final = true;
        }
        // 表示は直近3往復まで。それより古い分は捨てる(質問モードは短時間利用)。
        return next.slice(-3);
      });
    };

    const client = new TranslationWsClient({
      onMessage: handleMessage,
      onStateChange: setConn,
      onReconnected: () => {},
    });
    clientRef.current = client;
    // 質問は速さ優先。用語集プロファイルはサーバー側で全件強制されるため空でよい。
    // clientSessionId は本編と同じものを使い、質問も同じ講義メモに残す。
    client.start([], clientSessionId, 'speed', device, 'ja-zh');
    onClientChange(client);

    return () => {
      onClientChange(null);
      client.stop();
      clientRef.current = null;
    };
    // マウント時に1度だけ接続する。onClientChange は親の ref 代入のみで安定している。
  }, []);

  const latest = exchanges[exchanges.length - 1];
  const history = exchanges.slice(0, -1);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'var(--bg, #0d1117)',
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
      }}
    >
      <div className="row" style={{ alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: 16 }}>会話モード(日本語 → 中国語)</strong>
        <div className="spacer" />
        <button className="primary" style={{ minHeight: 44 }} onClick={onClose}>
          講義に戻る
        </button>
      </div>
      <p className="hint" style={{ margin: '0 0 12px' }}>
        日本語で話すと中国語訳が大きく表示されます。画面を先生に見せてください。
        {conn !== 'open' && '(接続中…)'}
      </p>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {history.map((x) => (
          <div key={x.segmentId} style={{ marginBottom: 10, opacity: 0.65 }}>
            <div style={{ fontSize: 13, color: 'var(--fg-dim, #8b949e)' }}>{x.ja}</div>
            <div style={{ fontSize: 22, lineHeight: 1.4 }}>{x.zh}</div>
          </div>
        ))}
        <div style={{ marginTop: 'auto' }}>
          {latest ? (
            <>
              <div style={{ fontSize: 15, color: 'var(--fg-dim, #8b949e)', marginBottom: 6 }}>
                {latest.ja || '(聞き取り中…)'}
              </div>
              <div
                style={{
                  fontSize: 44,
                  lineHeight: 1.35,
                  fontWeight: 700,
                  opacity: latest.final ? 1 : 0.6,
                }}
              >
                {latest.zh}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 20, color: 'var(--fg-dim, #8b949e)', textAlign: 'center', paddingBottom: 40 }}>
              🎤 話しかけてください
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
